import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { translations } from "@/i18n/translations";

const STORAGE_KEY = "provena_lang";
const SUPPORTED = ["fr", "en"];

const LanguageContext = createContext(null);

export const useLanguage = () => useContext(LanguageContext);

const detectInitial = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch {
    /* localStorage unavailable */
  }
  const nav = (navigator.language || "fr").toLowerCase();
  return nav.startsWith("en") ? "en" : "fr";
};

// Resolve a dotted key (e.g. "hero.subtitle") within the active dictionary,
// falling back to French then to the key itself.
const resolve = (dict, key) =>
  key.split(".").reduce((acc, part) => (acc == null ? undefined : acc[part]), dict);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(detectInitial);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED.includes(lang)) return;
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key) => {
      const val = resolve(translations[language], key);
      if (val !== undefined) return val;
      return resolve(translations.fr, key) ?? key;
    },
    [language]
  );

  // Like t(), but interpolates {placeholders} from vars into the resolved string.
  const tFmt = useCallback(
    (key, vars = {}) => {
      const str = t(key);
      if (typeof str !== "string") return str;
      return str.replace(/\{(\w+)\}/g, (_, name) =>
        vars[name] != null ? String(vars[name]) : ""
      );
    },
    [t]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, tFmt }),
    [language, setLanguage, t, tFmt]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageProvider;
